/* ============================================================
   EverythingStraps — Theme JS
   Vanilla ES6+, no dependencies
   ============================================================ */

'use strict';

/* ── Scroll Reveal ────────────────────────────────────────── */
function initScrollReveal() {
  if (!('IntersectionObserver' in window)) {
    document.querySelectorAll('[data-reveal]').forEach(el => el.classList.add('is-visible'));
    return;
  }
  const observer = new IntersectionObserver((entries) => {
    entries.forEach(entry => {
      if (entry.isIntersecting) {
        entry.target.classList.add('is-visible');
        observer.unobserve(entry.target);
      }
    });
  }, { threshold: 0.12, rootMargin: '0px 0px -40px 0px' });

  document.querySelectorAll('[data-reveal]:not(.is-visible)').forEach(el => observer.observe(el));
}
window.initScrollReveal = initScrollReveal;

/* ── Mobile Navigation ────────────────────────────────────── */
function initMobileNav() {
  const menuBtn = document.getElementById('mobile-menu-btn');
  const closeBtn = document.getElementById('mobile-nav-close');
  const nav = document.getElementById('mobile-nav');
  if (!menuBtn || !nav) return;

  function openNav() {
    nav.classList.add('is-open');
    nav.setAttribute('aria-hidden', 'false');
    menuBtn.setAttribute('aria-expanded', 'true');
    document.body.style.overflow = 'hidden';
  }

  function closeNav() {
    nav.classList.remove('is-open');
    nav.setAttribute('aria-hidden', 'true');
    menuBtn.setAttribute('aria-expanded', 'false');
    document.body.style.overflow = '';
  }

  menuBtn.addEventListener('click', openNav);
  if (closeBtn) closeBtn.addEventListener('click', closeNav);

  // Close on Escape
  document.addEventListener('keydown', (e) => {
    if (e.key === 'Escape' && nav.classList.contains('is-open')) closeNav();
  });

  // Close on outside click
  document.addEventListener('click', (e) => {
    if (nav.classList.contains('is-open') && !nav.contains(e.target) && !menuBtn.contains(e.target)) {
      closeNav();
    }
  });
}

/* ── Cart ─────────────────────────────────────────────────── */
const Cart = {
  cartBtn: null,
  cartClose: null,
  cartDrawer: null,
  cartOverlay: null,
  cartContent: null,

  init() {
    this.cartBtn = document.getElementById('cart-btn');
    this.cartClose = document.getElementById('cart-close');
    this.cartDrawer = document.getElementById('cart-drawer');
    this.cartOverlay = document.getElementById('cart-overlay');
    this.cartContent = document.getElementById('cart-drawer-content');

    if (!this.cartBtn) return;

    this.cartBtn.addEventListener('click', () => this.openDrawer());
    if (this.cartClose) this.cartClose.addEventListener('click', () => this.closeDrawer());
    if (this.cartOverlay) this.cartOverlay.addEventListener('click', () => this.closeDrawer());

    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && this.cartDrawer?.classList.contains('is-open')) this.closeDrawer();
    });

    // Quick add buttons on product cards
    document.addEventListener('click', (e) => {
      const quickAdd = e.target.closest('[data-quick-add]');
      if (!quickAdd) return;
      const variantId = quickAdd.dataset.quickAdd;
      if (!variantId) return;
      // If product has only one variant, add to cart directly
      if (quickAdd.textContent.trim() === 'Add to Cart') {
        e.preventDefault();
        this.addItem(variantId, 1);
      } else {
        // Navigate to product page for options
        const card = quickAdd.closest('.product-card');
        const link = card?.querySelector('a[href]');
        if (link) window.location = link.href;
      }
    });

    // Add to Cart button on product page
    const addBtn = document.getElementById('add-to-cart-btn');
    if (addBtn) {
      addBtn.addEventListener('click', () => {
        const variantSelect = document.getElementById('variant-select');
        const variantId = variantSelect ? variantSelect.value : addBtn.dataset.productId;
        if (variantId) this.addItem(variantId, 1, addBtn);
      });
    }

    // Initial cart count
    this.updateCount();
  },

  async openDrawer() {
    await this.renderDrawerContent();
    this.cartDrawer?.classList.add('is-open');
    this.cartOverlay?.classList.add('is-open');
    this.cartDrawer?.setAttribute('aria-hidden', 'false');
    this.cartOverlay?.setAttribute('aria-hidden', 'false');
    document.body.style.overflow = 'hidden';
    this.cartClose?.focus();
  },

  closeDrawer() {
    this.cartDrawer?.classList.remove('is-open');
    this.cartOverlay?.classList.remove('is-open');
    this.cartDrawer?.setAttribute('aria-hidden', 'true');
    this.cartOverlay?.setAttribute('aria-hidden', 'true');
    document.body.style.overflow = '';
    this.cartBtn?.focus();
  },

  async addItem(variantId, quantity = 1, button = null) {
    if (button) {
      const originalText = button.textContent;
      button.textContent = 'Adding...';
      button.disabled = true;
      button.classList.add('is-loading');

      try {
        const response = await fetch('/cart/add.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
          body: JSON.stringify({ id: Number(variantId), quantity })
        });

        if (!response.ok) throw new Error('Add to cart failed');
        await response.json();
        await this.updateCount();
        this.showToast('Added to cart');
        this.openDrawer();
      } catch (err) {
        this.showToast('Could not add to cart');
        console.error(err);
      } finally {
        button.textContent = originalText;
        button.disabled = false;
        button.classList.remove('is-loading');
      }
    } else {
      try {
        await fetch('/cart/add.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
          body: JSON.stringify({ id: Number(variantId), quantity })
        });
        await this.updateCount();
        this.showToast('Added to cart');
        this.openDrawer();
      } catch (err) {
        this.showToast('Could not add to cart');
      }
    }
  },

  async updateCount() {
    try {
      const res = await fetch('/cart.js');
      const cart = await res.json();
      document.querySelectorAll('[data-cart-count]').forEach(el => {
        el.textContent = cart.item_count;
        el.style.display = cart.item_count > 0 ? 'flex' : 'none';
      });
      // Update aria label
      const cartBtn = document.getElementById('cart-btn');
      if (cartBtn) cartBtn.setAttribute('aria-label', `Open cart (${cart.item_count} items)`);
    } catch (e) {}
  },

  async renderDrawerContent() {
    if (!this.cartContent) return;
    try {
      const res = await fetch('/cart.js');
      const cart = await res.json();

      if (cart.item_count === 0) {
        this.cartContent.innerHTML = `
          <div class="cart-empty">
            <svg viewBox="0 0 24 24"><path d="M6 2L3 6v14a2 2 0 002 2h14a2 2 0 002-2V6l-3-4z"/><line x1="3" y1="6" x2="21" y2="6"/><path d="M16 10a4 4 0 01-8 0"/></svg>
            <p>Your cart is empty</p>
            <a href="/collections/all" class="btn btn--secondary" onclick="Cart.closeDrawer()">Browse Products</a>
          </div>
        `;
        return;
      }

      const itemsHtml = cart.items.map(item => `
        <div class="cart-item" data-line-key="${item.key}">
          <img
            class="cart-item__image"
            src="${item.image}"
            alt="${item.title}"
            loading="lazy"
            width="80"
            height="80"
          >
          <div class="cart-item__info">
            <p class="cart-item__name">${item.product_title}</p>
            ${item.variant_title && item.variant_title !== 'Default Title' ? `<p class="cart-item__variant">${item.variant_title}</p>` : ''}
            <p class="cart-item__price">${this.formatMoney(item.price)}</p>
            <div class="cart-item__qty">
              <button class="qty-btn" data-action="decrease" data-key="${item.key}" data-qty="${item.quantity}" aria-label="Decrease quantity">−</button>
              <span>${item.quantity}</span>
              <button class="qty-btn" data-action="increase" data-key="${item.key}" data-qty="${item.quantity}" aria-label="Increase quantity">+</button>
            </div>
          </div>
        </div>
      `).join('');

      this.cartContent.innerHTML = `
        ${(() => { const goal = 20000, rem = goal - cart.total_price; if (rem <= 0) return `<div class="cart-ship-bar cart-ship-bar--done"><p class="cart-ship-bar__msg"><strong>Free shipping unlocked</strong> on this order</p><div class="cart-ship-bar__track"><div class="cart-ship-bar__fill" style="width:100%"></div></div></div>`; const pct = Math.max(3, Math.min(100, cart.total_price / goal * 100)); return `<div class="cart-ship-bar"><p class="cart-ship-bar__msg">You're ${this.formatMoney(rem)} away from <strong>free shipping</strong></p><div class="cart-ship-bar__track"><div class="cart-ship-bar__fill" style="width:${pct}%"></div></div></div>`; })()}<div class="cart-drawer__items">${itemsHtml}</div>
        <div class="cart-drawer__footer">
          <div class="cart-subtotal">
            <span class="cart-subtotal__label">Subtotal</span>
            <span class="cart-subtotal__amount">${this.formatMoney(cart.total_price)}</span>
          </div>
          <p style="font-size:13px;color:var(--color-secondary);margin-bottom:var(--sp-3);text-align:center;">Shipping & taxes calculated at checkout</p>
          <a href="/checkout" class="cart-checkout-btn">Checkout</a>
        </div>
      `;

      // Qty buttons
      this.cartContent.querySelectorAll('.qty-btn').forEach(btn => {
        btn.addEventListener('click', async () => {
          const key = btn.dataset.key;
          const action = btn.dataset.action;
          const qty = parseInt(btn.dataset.qty);
          const newQty = action === 'increase' ? qty + 1 : Math.max(0, qty - 1);
          await this.updateQty(key, newQty);
        });
      });

    } catch (e) {
      this.cartContent.innerHTML = '<div style="padding:var(--sp-4);text-align:center;color:var(--color-secondary);">Could not load cart.</div>';
    }
  },

  async updateQty(key, quantity) {
    try {
      await fetch('/cart/change.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
        body: JSON.stringify({ id: key, quantity })
      });
      await this.updateCount();
      await this.renderDrawerContent();
    } catch (e) {}
  },

  formatMoney(cents) {
    const amount = (cents / 100).toFixed(2);
    return `$${amount} SGD`;
  },

  showToast(message) {
    const toast = document.getElementById('toast');
    if (!toast) return;
    toast.textContent = message;
    toast.classList.add('is-visible');
    setTimeout(() => toast.classList.remove('is-visible'), 2500);
  }
};

/* ── Product Page ─────────────────────────────────────────── */
function initProductPage() {
  const productJsonEl = document.getElementById('product-json');
  if (!productJsonEl) return;

  const product = JSON.parse(productJsonEl.textContent);
  const variantSelect = document.getElementById('variant-select');
  const priceEl = document.getElementById('product-price');
  const addBtn = document.getElementById('add-to-cart-btn');

  // Gallery thumbnails
  const thumbs = document.querySelectorAll('.product-thumb');
  const mainImg = document.getElementById('gallery-main-img');

  thumbs.forEach(thumb => {
    thumb.addEventListener('click', () => {
      thumbs.forEach(t => t.classList.remove('is-active'));
      thumb.classList.add('is-active');
      if (mainImg) {
        mainImg.src = thumb.dataset.src;
        if (thumb.dataset.srcset) mainImg.srcset = thumb.dataset.srcset;
      }
    });
  });

  // Variant options
  const optionContainers = document.querySelectorAll('[data-option-name]');

  function getSelectedOptions() {
    const options = [];
    optionContainers.forEach(container => {
      const active = container.querySelector('.variant-option.is-active');
      if (active) options.push(active.dataset.value);
    });
    return options;
  }

  function findVariant(options) {
    return product.variants.find(v => {
      return v.options.every((opt, i) => opt === options[i]);
    });
  }

  function updateForVariant(variant) {
    if (!variant) return;

    // Update select
    if (variantSelect) variantSelect.value = variant.id;

    // Update price
    if (priceEl) {
      if (variant.compare_at_price && variant.compare_at_price > variant.price) {
        priceEl.innerHTML = `<span>${formatMoney(variant.price)}</span> <s style="color:var(--color-secondary);font-size:16px;margin-left:6px;">${formatMoney(variant.compare_at_price)}</s>`;
      } else {
        priceEl.textContent = formatMoney(variant.price);
      }
    }

    // Update add to cart
    if (addBtn) {
      addBtn.disabled = !variant.available;
      addBtn.textContent = variant.available ? 'Add to Cart' : 'Out of Stock';
    }

    // Update selected label text
    optionContainers.forEach((container, i) => {
      const optionName = container.dataset.optionName;
      const labelSpan = document.getElementById(`selected-${optionName}`);
      if (labelSpan) labelSpan.textContent = variant.options[i];
    });

    // Update URL
    const url = new URL(window.location.href);
    url.searchParams.set('variant', variant.id);
    window.history.replaceState({}, '', url);

    // Update main image if variant has featured image
    if (variant.featured_image && mainImg) {
      mainImg.src = variant.featured_image.src;
    }
  }

  function formatMoney(cents) {
    return '$' + (cents / 100).toFixed(2) + ' SGD';
  }

  optionContainers.forEach(container => {
    container.querySelectorAll('.variant-option').forEach(btn => {
      btn.addEventListener('click', () => {
        // Update active state
        container.querySelectorAll('.variant-option').forEach(b => {
          b.classList.remove('is-active');
          b.setAttribute('aria-pressed', 'false');
        });
        btn.classList.add('is-active');
        btn.setAttribute('aria-pressed', 'true');

        // Find and update variant
        const selectedOptions = getSelectedOptions();
        const variant = findVariant(selectedOptions);
        if (variant) updateForVariant(variant);
      });
    });
  });
}

/* ── Accordions ───────────────────────────────────────────── */
function initAccordions() {
  // Product page accordions
  document.querySelectorAll('.accordion-trigger').forEach(trigger => {
    trigger.addEventListener('click', () => {
      const expanded = trigger.getAttribute('aria-expanded') === 'true';
      const content = document.getElementById(trigger.getAttribute('aria-controls'));

      trigger.setAttribute('aria-expanded', String(!expanded));
      if (content) content.setAttribute('aria-hidden', String(expanded));
    });
  });

  // FAQ accordions
  document.querySelectorAll('.faq-question').forEach(trigger => {
    trigger.addEventListener('click', () => {
      const expanded = trigger.getAttribute('aria-expanded') === 'true';
      const content = document.getElementById(trigger.getAttribute('aria-controls'));

      trigger.setAttribute('aria-expanded', String(!expanded));
      if (content) content.setAttribute('aria-hidden', String(expanded));
    });
  });
}

/* ── Sticky Header Shadow ─────────────────────────────────── */
function initStickyHeader() {
  const header = document.querySelector('.site-header');
  if (!header) return;
  const update = () => {
    if (window.scrollY > 10) {
      header.style.borderBottomColor = 'rgba(210, 210, 215, 0.8)';
    } else {
      header.style.borderBottomColor = 'rgba(210, 210, 215, 0.5)';
    }
  };
  window.addEventListener('scroll', update, { passive: true });
}

/* ── Init ─────────────────────────────────────────────────── */
document.addEventListener('DOMContentLoaded', () => {
  initScrollReveal();
  initMobileNav();
  Cart.init();
  initProductPage();
  initAccordions();
  initStickyHeader();
});
